import { Autocomplete, type AutocompleteValue, CircularProgress, TextField, Typography } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

interface BasisProps<T> {
  naam: string;
  label: string;
  optieLabel: (item: T) => string;
  optieOndertitel?: ((item: T) => string | null | undefined) | undefined;
  verplicht?: boolean | undefined;
  hulptekst?: string | undefined;
  uitgeschakeld?: boolean | undefined;
  autoFocus?: boolean | undefined;
  extra?: ReactNode | undefined;
}

interface ZoekProps<T> extends BasisProps<T> {
  sleutel: string;
  laad: (term: string) => Promise<T[]>;
}

interface LijstProps<T> extends BasisProps<T> {
  opties: readonly T[];
  laden?: boolean | undefined;
}

/**
 * Wacht even met een waarde doorgeven tot de gebruiker stopt met typen.
 * @param waarde De waarde.
 * @param ms De wachttijd.
 * @returns De uitgestelde waarde.
 */
function useUitgesteld<T>(waarde: T, ms: number): T {
  const [uitgesteld, setUitgesteld] = useState(waarde);
  useEffect(() => {
    const timer = setTimeout(() => setUitgesteld(waarde), ms);
    return () => clearTimeout(timer);
  }, [waarde, ms]);
  return uitgesteld;
}

function Keuze<T extends { id: string }, Meerdere extends boolean>({
  naam,
  label,
  optieLabel,
  optieOndertitel,
  verplicht,
  hulptekst,
  uitgeschakeld,
  autoFocus,
  extra,
  meerdere,
  opties,
  laden,
  onTerm,
}: BasisProps<T> & {
  meerdere: Meerdere;
  opties: readonly T[];
  laden: boolean;
  onTerm?: ((term: string) => void) | undefined;
}) {
  const { control } = useFormContext();
  return (
    <Controller
      name={naam}
      control={control}
      {...(verplicht ? { rules: { required: 'Dit veld is verplicht' } } : {})}
      render={({ field, fieldState }) => (
        <Autocomplete<T, Meerdere, false, false>
          multiple={meerdere}
          options={opties as T[]}
          value={(field.value ?? (meerdere ? [] : null)) as AutocompleteValue<T, Meerdere, false, false>}
          onChange={(_gebeurtenis, waarde) => field.onChange(waarde)}
          onBlur={field.onBlur}
          getOptionLabel={(optie) => optieLabel(optie)}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          filterOptions={onTerm ? (x) => x : undefined}
          onInputChange={(_gebeurtenis, waarde, reden) => {
            if (onTerm && (reden === 'input' || reden === 'clear')) {
              onTerm(waarde);
            }
          }}
          loading={laden}
          disabled={uitgeschakeld ?? false}
          noOptionsText="Niets gevonden"
          loadingText="Laden…"
          renderOption={(eigenschappen, optie) => {
            const { key, ...rest } = eigenschappen as typeof eigenschappen & { key: string };
            const ondertitel = optieOndertitel?.(optie);
            return (
              <li key={key} {...rest}>
                <div>
                  <Typography variant="body2">{optieLabel(optie)}</Typography>
                  {ondertitel && (
                    <Typography variant="caption" color="text.secondary">
                      {ondertitel}
                    </Typography>
                  )}
                </div>
              </li>
            );
          }}
          renderInput={(parameters) => (
            <TextField
              {...parameters}
              label={label}
              required={verplicht ?? false}
              autoFocus={autoFocus ?? false}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message ?? hulptekst}
              size="small"
              slotProps={{
                ...parameters.slotProps,
                input: {
                  ...parameters.slotProps.input,
                  endAdornment: (
                    <>
                      {laden ? <CircularProgress color="inherit" size={16} /> : null}
                      {extra}
                      {parameters.slotProps.input.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
      )}
    />
  );
}

/**
 * Een keuzeveld dat zoekt in de API terwijl de gebruiker typt. De waarde in het formulier is het gekozen
 * record zelf (of `null`), zodat het label bekend is; bij het verzenden gaat alleen het id mee.
 * @param props De naam, het label, een sleutel voor de cache en de zoekfunctie.
 * @returns Het veld.
 */
export function ZoekKeuzeveld<T extends { id: string }>(props: ZoekProps<T>) {
  const [term, setTerm] = useState('');
  const uitgesteld = useUitgesteld(term, 250);
  const query = useQuery({
    queryKey: ['zoekveld', props.sleutel, uitgesteld],
    queryFn: () => props.laad(uitgesteld),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  return (
    <Keuze<T, false> {...props} meerdere={false} opties={query.data ?? []} laden={query.isFetching} onTerm={setTerm} />
  );
}

/**
 * Als `ZoekKeuzeveld`, maar met meerdere keuzes. De waarde in het formulier is een lijst records.
 * @param props De naam, het label, een sleutel voor de cache en de zoekfunctie.
 * @returns Het veld.
 */
export function ZoekMeerkeuzeveld<T extends { id: string }>(props: ZoekProps<T>) {
  const [term, setTerm] = useState('');
  const uitgesteld = useUitgesteld(term, 250);
  const query = useQuery({
    queryKey: ['zoekveld', props.sleutel, uitgesteld],
    queryFn: () => props.laad(uitgesteld),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  return (
    <Keuze<T, true> {...props} meerdere={true} opties={query.data ?? []} laden={query.isFetching} onTerm={setTerm} />
  );
}

/**
 * Een keuzeveld over een lijst die al geladen is, bijvoorbeeld de contactpersonen van een bedrijf.
 * @param props De naam, het label en de opties.
 * @returns Het veld.
 */
export function LijstKeuzeveld<T extends { id: string }>(props: LijstProps<T>) {
  return <Keuze<T, false> {...props} meerdere={false} laden={props.laden ?? false} />;
}
